import type { PremiseSummary } from "../http/foyer";

/**
 * A product is something a customer BUYS for a place. It decides which areas of
 * the CMS exist for that place - nothing else does.
 *
 * This replaced the old hotel/care premise type. A place is just a place; what
 * you can do with it comes from what was sold. `segment` still exists in the
 * data because ~40 server rules key off it (no casting in care, occupancy from
 * admission, the PDL audit trail), but it is now DERIVED from the Care product
 * rather than picked by an operator.
 */
/*
 * `ProductKey` and the licence alias table moved to `@shared/products` when the
 * SERVER needed the same answer: a live TV channel may be placed on a signage
 * screen only where Streaming TV was sold, and a rule only the client enforces
 * is a rule anyone can skip with curl. Re-exported here so the forty existing
 * importers of this module keep working.
 */
export {
  LICENSE_PRODUCT_MAP,
  productsFromLicenses,
  canPlaceLiveTvOnSignage,
  SIGNAGE_LIVE_TV_PRODUCTS,
  type ProductKey,
} from "@shared/products";
import { productsFromLicenses, type ProductKey } from "@shared/products";

/** Display names, in the order a place is usually sold them. */
export const PRODUCT_LABELS: Record<ProductKey, string> = {
  "streaming-tv": "Streaming TV",
  frontlayer: "Frontlayer",
  promotions: "Promotions",
  signage: "Signage",
  care: "Care",
  "video-call": "Video calling",
};

export const PRODUCT_ORDER: ProductKey[] = [
  "streaming-tv",
  "frontlayer",
  "promotions",
  "signage",
  "care",
  "video-call",
];

/**
 * Route prefixes whose content belongs to ONE premise. Defined here, once:
 * three copies of this list had already drifted apart.
 */
export const PREMISE_SCOPED_PREFIXES = [
  "/places",
  "/streaming-tv",
  "/frontlayer",
  "/guest-web",
  "/promotions",
  "/signage",
  "/care",
  "/video-calling",
];

/**
 * Sections that mean something with a customer scoped and NO location.
 *
 * Product areas are normally per-location, so with no location picked a group
 * has nothing to offer and is hidden. These are the exceptions: the shared
 * media library and chain-level signage authoring are facts about the CUSTOMER.
 * A group is shown at customer scope only if it has at least one of these, and
 * only these appear inside it - a menu that lists sections you cannot open is
 * worse than one that lists none.
 */
export const CHAIN_CAPABLE_SECTIONS = new Set([
  // Frontlayer calls it "media", Signage calls it "library" - one panel.
  "media",
  "library",
  // Offers are scoped premise/company/global, so a chain authors them with no
  // location picked (POS-14).
  "offers",
  "plan",
  "campaigns",
  "channels",
  // A screen group's whole reason to exist is crossing locations - "every
  // reception in the chain". It is the one section here that is MORE useful
  // with no location picked than with one, and leaving it out made a
  // company-layer group unreachable from the only scope that can create one.
  "groups",
  // A chain designs one brand template for every location it owns, which is a
  // fact about the CUSTOMER and exactly what this set is for.
  "templates",
  // Offers has a company layer (docs/room-offers.md §8), so a chain authoring
  // one spa package for every hotel it owns has to be able to reach the page
  // with a customer scoped and NO location. Without this the job is
  // unreachable, and POS-14 ("author once for the chain, override per hotel")
  // is built on top of it.
  "offers",
  // And the queue those offers produce (POS-9): a duty manager covering four
  // hotels reads one page with four named queues on it, rather than re-scoping
  // the header four times to find out whether anybody is waiting.
  "requests",
  // And what the two of them earned (POS-12). The chain layer is the whole
  // point of the page for a group: one row per hotel, ranked, is the question
  // only an owner can ask, and it is unreachable without a no-location branch.
  "revenue",
]);

export interface ProductArea {
  key: ProductKey;
  label: string;
  /** Route prefix owning this product's sections. */
  base: string;
}

/** The products that contribute a section to the menu. */
export const PRODUCT_AREAS: ProductArea[] = [
  { key: "streaming-tv", label: "Streaming TV", base: "/streaming-tv" },
  { key: "frontlayer", label: "Frontlayer", base: "/frontlayer" },
  { key: "promotions", label: "Promotions", base: "/promotions" },
  { key: "signage", label: "Signage", base: "/signage" },
  { key: "care", label: "Care", base: "/care" },
];

/**
 * Products in force for one premise.
 *
 * Licenses are the ONLY source. Nothing is ever implied - not Frontlayer for
 * every place, not Care for a care-segment place. A product that appears
 * without having been sold is a menu that promises something nobody bought,
 * and it hides the one question worth asking: what does this customer have?
 *
 * The cost of that honesty is that a place with no licenses has no product
 * sections at all. That is the correct answer, and the fix is to record what
 * was sold - on the place's Products page.
 */
export function productsForPremise(
  premise: Pick<PremiseSummary, "products"> | undefined
): Set<ProductKey> {
  return productsFromLicenses(premise?.products ?? []);
}

/**
 * Products in force anywhere in a CUSTOMER - the union across its locations.
 *
 * Deliberately different from {@link productsForPremise}, and used for a
 * different question. `productsForPremise` answers "what did this place buy?",
 * where a union would describe a place that does not exist. This answers "does
 * this customer do signage anywhere?", which is a fact about the customer, and
 * is what decides whether chain-level authoring is reachable with a customer
 * scoped and no location.
 *
 * Only the areas that genuinely have a customer-level surface should consult
 * it. A union must never be used to unlock a per-location editor.
 */
export function productsForCompany(
  companyId: string | undefined,
  premises: Pick<PremiseSummary, "companyId" | "products">[]
): Set<ProductKey> {
  const out = new Set<ProductKey>();
  if (!companyId) return out;
  for (const p of premises) {
    if (p.companyId !== companyId) continue;
    for (const key of productsForPremise(p)) out.add(key);
  }
  return out;
}

/** A place's products as display names, in catalogue order. */
export function productLabelsFor(
  premise: Pick<PremiseSummary, "products"> | undefined
): string[] {
  const set = productsForPremise(premise);
  return PRODUCT_ORDER.filter((k) => set.has(k)).map((k) => PRODUCT_LABELS[k]);
}

/**
 * What kind of place this is, read off its products rather than a stored type.
 *
 * Frontlayer is the base: with Care it is a care place, without it a hotel.
 * The add-ons that change how the place is run get their own pill, so the
 * column answers "what is this?" and "what else did they buy?" at a glance.
 * A place with no Frontlayer has no base kind - a signage-only site is not a
 * hotel with the TVs switched off.
 */
export interface PremiseTypeChip {
  label: string;
  kind: "base" | "addon";
}

export function premiseTypeChips(
  premise: Pick<PremiseSummary, "products"> | undefined
): PremiseTypeChip[] {
  const p = productsForPremise(premise);
  const out: PremiseTypeChip[] = [];
  if (p.has("frontlayer")) {
    out.push({ label: p.has("care") ? "Care" : "Hotel", kind: "base" });
  }
  if (p.has("signage")) out.push({ label: "Signage", kind: "addon" });
  if (p.has("video-call")) out.push({ label: "VC", kind: "addon" });
  return out;
}
